import React from "react";
import { sydneyWhen } from "./time.js";

// Each horse against the whole sale, as bands, in the Inglis admin console only
// (DECISIONS.md, 26 Sep 2026; built for vendors on 25 Sep and moved the next
// day). The host computes every band against the whole sale through
// admin.stats.standing@1; vendors are sent no standing at all. The wording is
// the wording three vendor reviews settled on: a band is a claim that is true of
// the whole sale, a tie never rounds a horse up, and a lower band is never shown
// as the horse "going down", because a horse never loses a buyer's vet.

// Ladder order, which is also list order. "Too early to rank" always means at
// least one buyer's vet, so it sits above "none yet": before 30 horses are
// opened, the horses vets are looking at must still lead the list (dev, 26 Sep
// 2026, where they had sunk below every "X-rays not up").
export const BANDS = ["top10", "top25", "tophalf", "some", "too_early", "none", "noxray"];

const LABEL = {
  top10: "Top 10%",
  top25: "Top 25%",
  tophalf: "Top half",
  some: "Some interest",
  none: "No buyer’s vet yet",
  noxray: "X-rays not up",
  too_early: "Too early to rank",
};

// Positional bands only. None, noxray and too_early are not places on the
// ladder, so a move to or from them is never "up" or "fell".
const RANK = { top10: 4, top25: 3, tophalf: 2, some: 1 };

export const bandLabel = (band) => LABEL[band] || "—";

function meaning(band, lodgementsKnown) {
  switch (band) {
    case "top10": return "Ahead of at least 9 in 10 horses in the sale";
    case "top25": return "Ahead of at least 3 in 4";
    case "tophalf": return "Ahead of at least half";
    case "some": return "At least one buyer’s vet; not yet in the top half";
    case "none": return lodgementsKnown
      ? "X-rays are up; no buyer’s vet has opened them"
      : "No buyer’s vet has opened the X-rays";
    case "noxray": return "Nothing to open yet, so not ranked";
    case "too_early": return "Bands start once enough of the sale has been vetted";
    default: return "";
  }
}

const vetsText = (value) => (typeof value === "number" && value > 0
  ? `${value} buyer’s vet${value === 1 ? "" : "s"}`.replace("buyer’s vets", "buyers’ vets")
  : "");

export function movement(entry) {
  if (!entry) return null;
  const now = RANK[entry.band];
  const before = RANK[entry.yesterdayBand];
  if (now && (entry.yesterdayBand === "none" || (before && now > before))) return "up";
  if (now && before && now < before) return "fell";
  return null;
}

export function BandChip({ band, stale, large, label }) {
  const classes = ["band-chip", `band-${band}`];
  if (stale) classes.push("stale");
  if (large) classes.push("large");
  return <span className={classes.join(" ")} data-band={band}>{label || bandLabel(band)}</span>;
}

/** The "In the sale" cell of the lots list. */
export function StandingCell({ entry, stale, viewed }) {
  if (!entry) return <span className="muted">—</span>;
  const move = movement(entry);
  return (
    <span className="standing-cell">
      <BandChip band={entry.band} stale={stale}/>
      {move === "up" && <span className="standing-up" title="Moved up since this time yesterday">▲ up</span>}
      {move === "fell" && <span className="standing-was">was {bandLabel(entry.yesterdayBand)}</span>}
      {entry.buyersVets > 0 && <span className="standing-vets">{vetsText(entry.buyersVets)}</span>}
      {entry.sinceYesterday > 0 && <span className="standing-new">+{entry.sinceYesterday} since yesterday</span>}
      {entry.band === "none" && viewed && <span className="standing-was">opened, not by a buyer’s vet</span>}
    </span>
  );
}

/** Where the standing sits and how fresh it is: said once, not per row. */
export function standingHeadline(standing) {
  if (!standing || standing.status === "unavailable") return null;
  const when = sydneyWhen(standing.asOf);
  return standing.stale
    ? `Standing against the sale as at ${when} — not updated since`
    : `Standing against the sale · counting since the catalogue went live · updated ${when}`;
}

/** Early days, too early and X-rays not up: the notes above the list. */
export function StandingNotes({ standing, numbers }) {
  if (!standing) return null;
  if (standing.status === "unavailable") {
    return (
      <p className="standing-note" role="status">
        Standing not available yet — we have not been able to read the sale’s X-ray views.
      </p>
    );
  }
  const notUp = Object.entries(standing.lots || {})
    .filter(([, entry]) => entry.band === "noxray")
    .map(([lotId]) => numbers[lotId] || lotId)
    .sort((a, b) => String(a).localeCompare(String(b), undefined, { numeric: true }));
  return (
    <>
      {standing.status === "too_early" && (
        <p className="standing-note">
          <strong>Too early to rank.</strong> Bands start once 30 horses in the sale have been
          opened by a buyer’s vet. Each horse’s buyers’ vets still show; only the comparison waits.
        </p>
      )}
      {standing.status === "ranked" && standing.earlyDays && (
        <p className="standing-note">
          <strong>Early days.</strong> Less than a third of the sale has been vetted, so bands will
          move as buyers’ vets get to more horses. A horse can drop a band without anything
          changing on it.
        </p>
      )}
      {notUp.length > 0 && (
        <p className="standing-note action">
          <strong>{notUp.length === 1 ? "1 horse has" : `${notUp.length} horses have`} no X-rays up</strong>
          {/* Across a whole sale the list would run to hundreds; name them only when short. */}
          {notUp.length <= 20 ? <> — {notUp.length === 1 ? "Lot" : "Lots"} {joinLots(notUp)}</> : null}. Nobody can vet{" "}
          {notUp.length === 1 ? "it" : "them"} until {notUp.length === 1 ? "it is" : "they are"} lodged.
        </p>
      )}
    </>
  );
}

function joinLots(numbers) {
  if (numbers.length === 1) return numbers[0];
  return `${numbers.slice(0, -1).join(", ")} and ${numbers[numbers.length - 1]}`;
}

/** What the bands mean, and what is counted. Collapsible where space is short. */
export function BandLegend({ standing, collapsible, defaultOpen = false }) {
  const [open, setOpen] = React.useState(!collapsible || defaultOpen);
  if (!standing || standing.status === "unavailable") return null;
  const known = standing.lodgements === "known";
  const bands = known ? BANDS : BANDS.filter((band) => band !== "noxray");
  const body = (
    <div className="band-legend-body">
      <ul className="band-legend-list">
        {bands.map((band) => (
          <li key={band}><BandChip band={band}/><span>{meaning(band, known)}</span></li>
        ))}
      </ul>
      <div className="band-legend-notes">
        <p>
          <strong>Buyers’ vets</strong>: different vets who opened the X-rays with a buyer nominated.
          {known
            ? " The vet who lodged the X-rays, and anyone who opened them without nominating a buyer, is not counted."
            : " Anyone who opened them without nominating a buyer is not counted. The Repository doesn’t yet tell us who lodged each horse’s X-rays, so every horse is ranked, and a lodging vet who nominated a buyer would be counted."}
          {" "}Opening again does not count again. A tie never rounds a horse up.
        </p>
        <p>
          Why vets and not buyers: buyers are named by each vet as they open the X-rays, some names
          are not released yet, and the same buyer can be written differently by two practices —
          so buyers cannot be matched up across the sale. The vet can.
        </p>
        <p>Inglis only: vendors are not shown any standing, theirs or anyone else’s.</p>
      </div>
    </div>
  );
  if (!collapsible) return <section className="band-legend">{body}</section>;
  return (
    <section className="band-legend collapsible">
      <button type="button" className="band-legend-toggle" aria-expanded={open ? "true" : "false"}
              onClick={() => setOpen((value) => !value)}>What the bands mean</button>
      {open && body}
    </section>
  );
}

/** The lots grouped by band: the owner-call view. */
export function ByBand({ standing, lots, numbers, horses, onOpen }) {
  const known = standing.lodgements === "known";
  const groups = BANDS
    .filter((band) => known || band !== "noxray")
    .map((band) => ({
      band,
      lots: lots.filter((lot) => standing.lots?.[lot.lotId]?.band === band),
    }))
    .filter((group) => group.lots.length > 0);
  return (
    <div className="by-band">
      {groups.map((group) => (
        <section key={group.band} className="by-band-rung">
          <div className="by-band-head">
            <BandChip band={group.band} stale={standing.stale}/>
            <span className="muted">{group.lots.length} {group.lots.length === 1 ? "horse" : "horses"}</span>
            <span className="by-band-meaning">{meaning(group.band, known)}</span>
          </div>
          <div className="by-band-lots">
            {group.lots.map((lot) => {
              const entry = standing.lots[lot.lotId];
              const move = movement(entry);
              return (
                <button key={lot.lotId} type="button" className="by-band-lot" onClick={() => onOpen(String(lot.lotId))}
                        aria-label={`Open lot ${numbers[lot.lotId]}`}>
                  <span className="by-band-number">Lot {numbers[lot.lotId]}</span>
                  {horses[lot.lotId] && <span className="by-band-horse">{horses[lot.lotId]}</span>}
                  {move === "up" && <span className="standing-up">▲ up</span>}
                </button>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}

const SENTENCE = {
  top10: "This horse has had more buyers’ vets on its X-rays than nine in every ten horses in the sale.",
  top25: "This horse has had more buyers’ vets on its X-rays than three in every four horses in the sale.",
  tophalf: "This horse has had more buyers’ vets on its X-rays than half the horses in the sale.",
  some: "Buyers’ vets have opened this horse’s X-rays, but it is not yet in the top half of the sale.",
  none: "No buyer’s vet has opened this horse’s X-rays yet.",
  noxray: "This horse’s X-rays are not up in the Repository, so nobody can vet it yet. It is not ranked.",
  too_early: "Too early to rank: not enough of the sale has been vetted yet. This horse’s buyers’ vets are counted here all the same.",
};

const DAY = new Intl.DateTimeFormat("en-US", { timeZone: "Australia/Sydney", weekday: "short", day: "numeric" });
function evening(at) {
  const parts = Object.fromEntries(DAY.formatToParts(new Date(at)).map((part) => [part.type, part.value]));
  return `${parts.weekday} ${parts.day}`;
}

/** The lot page's standing panel. */
export function StandingPanel({ standing, lotId }) {
  if (!standing || standing.status === "unavailable") return null;
  const entry = standing.lots?.[lotId];
  if (!entry) return null;
  const move = movement(entry);
  const { noBuyer = 0, lodgingVet = 0 } = entry.uncounted || {};
  const skipped = noBuyer + lodgingVet;
  const reasons = [
    noBuyer ? `${noBuyer} opened without a buyer nominated` : null,
    lodgingVet ? `${lodgingVet} by the vet who lodged the X-rays` : null,
  ].filter(Boolean).join(", ");
  return (
    <section className="standing-panel" aria-label="In the sale">
      <div className="standing-panel-main">
        <div className="standing-panel-band">
          <span className="eyebrow">In the sale</span>
          <div className="standing-panel-chip">
            <BandChip band={entry.band} stale={standing.stale} large/>
            {entry.buyersVets > 0 && <span>{vetsText(entry.buyersVets)}</span>}
          </div>
          <p className="standing-sentence">{SENTENCE[entry.band]}</p>
          {skipped > 0 && (
            <p className="caption">
              <strong>{skipped} X-ray {skipped === 1 ? "view" : "views"} not counted:</strong> {reasons}.
              {" "}{skipped === 1 ? "It is" : "They are"} still listed under Activity below.
            </p>
          )}
          {standing.stale && <p className="caption">As at {sydneyWhen(standing.asOf)} — not updated since.</p>}
        </div>
        <div className="standing-panel-since">
          <span className="qstat-label">Since this time yesterday</span>
          <span><strong>{entry.sinceYesterday}</strong> new {entry.sinceYesterday === 1 ? "buyer’s vet" : "buyers’ vets"}</span>
          {move === "up" && <span className="muted">Up from {bandLabel(entry.yesterdayBand)}</span>}
          {move === "fell" && (
            <span>
              Was {bandLabel(entry.yesterdayBand)}. <strong>Nothing has changed on this horse</strong> — more
              horses in the sale have been vetted since, and some have passed it.
            </span>
          )}
          {!move && <span className="muted">Band unchanged</span>}
        </div>
      </div>
      <div className="standing-history">
        <span className="qstat-label">Last seven days — as at 6 pm each day, and now</span>
        <ol>
          {(entry.history || []).map((cell, index, all) => (
            <li key={cell.at}>
              <span className="muted">{index === all.length - 1 ? "Now" : evening(cell.at)}</span>
              <BandChip band={cell.band}/>
            </li>
          ))}
        </ol>
      </div>
      <BandLegend standing={standing} collapsible/>
    </section>
  );
}
